import type { TodoActionDTO } from "@/hooks/use-agent-todos";
/**
 * EOS L10 — TypeScript types mirroring the backend contract.
 *
 * Backend source-of-truth: `hickory-ui/backend/app/schemas/` (Slice A).
 * These are the wire types the frontend consumes from `/l10/*`, `/issues/*`,
 * `/headlines/*`, `/cascades/*`. Kept thin — no Zod runtime checks for
 * beta-1 since polling re-runs anyway.
 */

// ============================================================================
// Enums (mirror backend Postgres enums + Python literals)
// ============================================================================

export const AGENDA_KINDS = [
  "segue",
  "scorecard",
  "rocks",
  "headlines",
  "todos",
  "ids",
  "conclude",
] as const;
export type AgendaKind = (typeof AGENDA_KINDS)[number];

/**
 * One row of a team's EFFECTIVE agenda — mirrors the backend's
 * ``app.schemas.l10.AgendaItem``. Ordered; ``minutes`` is the section's
 * budget in minutes (seconds = ``minutes * 60``); exactly one enabled row
 * carries ``flex: true`` (the elastic section, IDS today). A disabled row
 * stays in the list (so it can be re-enabled without losing its position)
 * but must never be reachable via stepper / keyboard / prev-next.
 */
export interface AgendaItem {
  kind: AgendaKind;
  minutes: number;
  enabled: boolean;
  flex: boolean;
}

export const SESSION_STATUSES = [
  "scheduled",
  "in_progress",
  "completed",
  "abandoned",
] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

export const ISSUE_STATUSES = [
  "open",
  "identified",
  "discussing",
  "solved",
  "parked",
  "dropped",
] as const;
export type IssueStatus = (typeof ISSUE_STATUSES)[number];

export type TimeHorizon = "short" | "long";

export type IssueSourceType =
  | "scorecard_kpi"
  | "rock"
  | "headline"
  | "manual"
  // Pushed in from a Kory artifact's "Add to L10" action (the artifact
  // composer stamps issues with this provenance). Backend ``source_type`` is
  // a free string, so no enum/migration is tied to this value.
  | "kory_artifact"
  // Created via the Kory MCP tool (meeting_create_issue from Kory chat).
  | "kory"
  // Promoted from a to-do via the cross-create "Drop on Issues" split button.
  // Backend source_type is free text — no DDL needed for this value.
  | "todo"
  // Staged by a Drive run (the gatekeeper stamps source_type='drive').
  | "drive"
  // A cross-team copy (``copy_issue_to`` — Move/Copy's copy leg); source_ref
  // carries the SOURCE ISSUE id, from which the read model resolves
  // ``copied_from_team_name``.
  | "cross_l10_copy"
  // Created from an M&A integration task (the /mna integrations drill-down);
  // source_ref carries "{tenant_id}:{task}". Backend source_type is free text —
  // no DDL needed for this value.
  | "mna_integration_task"
  | "migrated_from_ninety";

export type HeadlineKind = "none" | "customer" | "employee" | "company" | "cascade";
export type Sentiment = "positive" | "neutral" | "negative";
export type CascadeStatus = "pending" | "delivered" | "consumed";

// ============================================================================
// Domain entities
// ============================================================================

/**
 * Which scorecard a team's L10 SCORECARD section embeds. Each kind maps to
 * a route the runner navigates to (and an embed component the runner
 * inlines). Configurable per team — leadership uses `eos_weekly`, brand
 * GMs will typically use `csr_scorecard` or `service_scorecard`, etc.
 */
export const SCORECARD_KINDS = [
  "eos_weekly",
  "ai_scorecard",
  "csr_scorecard",
  "service_scorecard",
  "sales_scorecard",
  "sales_marketing_scorecard",
  "team_scorecard",
] as const;
export type ScorecardKind = (typeof SCORECARD_KINDS)[number];

export interface L10Team {
  id: string;
  name: string;
  recurring_day_of_week: number | null; // 0=Mon; null = no recurring schedule
  recurring_time: string | null; // "HH:MM"; null = no recurring schedule
  recurring_tz: string;
  default_duration_minutes: number;
  /**
   * Per-section budgets in seconds. Sum should equal
   * `default_duration_minutes * 60` but isn't enforced — facilitators can
   * trade time across sections. Backend supplies sane defaults via
   * `DEFAULT_SECTION_BUDGETS_SECONDS` for any unspecified section so a
   * new team without overrides still gets a working agenda.
   */
  section_budgets_seconds: Record<AgendaKind, number>;
  /**
   * Which scorecard variant the SCORECARD section embeds. RBAC at the
   * HickoryOS level scopes which rows the user can see; this only picks
   * WHICH scorecard surface to render.
   */
  scorecard_kind: ScorecardKind;
  /** Which week the meeting's scorecard opens on. Default "last_week" —
   *  the room reviews last week's numbers. */
  scorecard_week_default?: "last_week" | "current_week";
  /**
   * Google Meet space code bound to this team (the bare `xxx-yyyy-zzz`
   * code, not a URL), or `null` when no Meet is bound. Drives the runner's
   * Join Meet button + "Meet is live" badge.
   */
  meet_space_code: string | null;
  /**
   * Ordered list of HickoryOS route links shown as extra scorecard tabs
   * in the runner. Each item has a ``path`` (relative) and optional ``label``.
   * Empty array when none are configured.
   */
  scorecard_links?: { path: string; label?: string }[];
  /**
   * Customer-brand tenant slugs derived from the team's non-observer roster
   * (observers + internal/WMS tenants excluded). The runner defaults the
   * scorecard + customer-recognition brand filters to these. Empty array when
   * the team has no customer-brand members (the runner then leaves filters at
   * their default). Optional: the wire mapper always fills it (`?? []`), but
   * older cached payloads / test fixtures may omit it.
   */
  brand_scope?: string[];
  /**
   * The team's EFFECTIVE agenda — ordered, always populated by the backend
   * (the stored custom agenda when set, else derived from the meeting-length
   * template). Optional here only because older cached payloads / mock
   * fixtures / test doubles may omit it; the runner falls back to
   * ``AGENDA_KINDS`` + ``section_budgets_seconds`` when absent, which
   * reproduces this same shape for today's all-derived-default teams.
   */
  agenda?: AgendaItem[];
  /** True iff the team has a stored custom agenda rather than the derived
   *  default. Optional/undefined is treated as ``false``. */
  agenda_is_custom?: boolean;
  /** "standard" for an ordinary weekly L10; a non-standard value (e.g.
   *  "quarterly_planning") marks a hidden backing team for a different
   *  meeting type — the runner branches its pre-session surface on this.
   *  Optional only so older cached payloads / test fixtures default to
   *  "standard" via the wire mapper. */
  container_kind?: string;
}

/**
 * Result of ``GET /l10/sessions/:id/meet-status`` — whether the team's bound
 * Google Meet has a live conference right now (backs the runner's "Meet is
 * live" badge). `is_live` is `false` (never an error) when no Meet is bound,
 * no organizer resolves, or the upstream check fails, so the badge just hides.
 */
export interface MeetSpaceStatus {
  is_live: boolean;
  active_conference: string | null;
}

/**
 * One row of ``GET /l10/teams`` — an L10 team the current user can run,
 * as rendered by the ``/meetings`` switcher. Lighter than ``L10Team``:
 * carries only the identity + schedule fields the switcher + slug
 * resolution need (no per-section budgets, no scorecard_kind — those are
 * fetched per-team once the runner mounts).
 */
export interface MyL10Team {
  id: string;
  name: string;
  /** URL-safe slug for ``/meetings/:teamSlug`` (e.g. "ai-technology"). */
  slug: string;
  tenant_id: string | null;
  recurring_day_of_week: number | null; // 0=Mon
  recurring_time: string | null; // "HH:MM"
  recurring_tz: string;
  default_duration_minutes: number;
  /** Whether the caller sits on this team's roster — drives the switcher's
   *  "Your meetings" section. */
  is_member: boolean;
  /** Whether this team is the caller's PRIMARY (home) L10 — at most one row.
   *  Person-centric defaults (Kory artifact pushes, the rocks register's
   *  initial team, the /meetings ordering) prefer it over an arbitrary pick. */
  is_primary: boolean;
  /** Whether the team has any issue/headline history — lets the switcher
   *  trail empty Central-reachable teams under "All teams". */
  has_activity: boolean;
  /** Whether the team is sealed — members-only, Central override suppressed.
   *  Drives the switcher's lock badge + a non-navigable card for non-members. */
  sealed: boolean;
  /** The team's currently-running (``in_progress``) session id, if any —
   *  drives the "In session now" live badge so a user knows they'll JOIN an
   *  in-progress meeting rather than start a duplicate. Null when none is live. */
  active_session_id: string | null;
  /** ``ended_at`` (ISO) of the team's most recent concluded meeting — drives
   *  the switcher's "Last met" line + recency sort. Null when never met. */
  last_met_at: string | null;
  /** Roster size — a cheap "how real is this team" signal on the switcher. */
  member_count: number;
  /** The team's EFFECTIVE agenda — see ``L10Team.agenda``. Optional for the
   *  same fallback reasons. */
  agenda?: AgendaItem[];
  /** True iff the team has a stored custom agenda. See ``L10Team.agenda_is_custom``. */
  agenda_is_custom?: boolean;
  /** Google Meet space code bound to this team (null = none). Lets the
   *  standalone recap page gate the transcript-pull button client-side
   *  instead of discovering the 422 on click. */
  meet_space_code: string | null;
}

export interface AttendeeProfile {
  id: string;
  display_name: string;
  role_title: string | null;
  /** Linked login account (``profile.user_id``), or null for a login-less
   *  member. Lets L10 pickers scope to this roster + gate login-required
   *  assignee pickers. */
  user_id: string | null;
  /** Whether THIS team is the member's primary (home) L10. Optional — only
   *  the roster-management surfaces care. */
  is_primary?: boolean;
  /** Whether this member is an OBSERVER — full meeting access, but excluded
   *  from attendance / props / tenancy stats. */
  is_observer?: boolean;
}

export interface L10Attendance {
  session_id: string;
  profile_id: string;
  present: boolean;
  rating_1_to_10: number | null;
  /** "What would make it a 10?" note paired with the rating. */
  rating_note: string | null;
}

export interface L10AgendaSection {
  session_id: string;
  kind: AgendaKind;
  started_at: string | null;
  ended_at: string | null;
  notes: string;
}

export interface L10SessionEvent {
  id: string;
  session_id: string;
  ts: string;
  actor_id: string | null;
  kind: string;
  payload: Record<string, unknown>;
}

export interface L10Session {
  /** The team's effective agenda snapshotted when this session started —
   *  the live meeting's authoritative section order. Absent on sessions
   *  created before the snapshot column existed (fall back to the team's
   *  effective agenda). */
  agenda?: AgendaItem[];
  id: string;
  team_id: string;
  facilitator_id: string;
  status: SessionStatus;
  started_at: string | null;
  ended_at: string | null;
  current_section: AgendaKind;
  sections: L10AgendaSection[];
  attendance: L10Attendance[];
  events: L10SessionEvent[];
  // Conclude "Props" shared view-state cursor — mirrored across every client on
  // the Conclude screen off the 2s session poll. The clicking client writes it
  // (last-write-wins); everyone renders FROM it. ``props_review_started_at``
  // anchors the reviewed card set (props given mid-review don't shift indices).
  props_phase: PropsPhase;
  props_index: number;
  props_review_started_at: string | null;
  // IDS shared discussion focus — same shared-view-state contract as the
  // Props cursor (any participant writes, last-write-wins, everyone mirrors
  // off the 2s poll). Null = never written this session; optional only so
  // older fixtures that predate the field still typecheck (the mapper
  // always sets it).
  ids_focus?: IdsFocus | null;
}

/** Conclude Props panel state-machine phase — the shared cursor's ``phase``. */
export type PropsPhase = "landing" | "reviewing" | "done" | "replaying";

/** The Issues section's shared pick/discussing overlay (``l10_sessions.ids_focus``). */
export interface IdsFocus {
  picking: boolean;
  /** In-flight pick order, max 3. */
  picked: string[];
  /** Committed Discussing set, in discuss order. */
  discussing: string[];
}

/** Team-wide thumbs tally on a Drive-produced item. */
export interface DriveFeedbackCounts {
  up: number;
  down: number;
}

/** The rock an L10 to-do or issue is linked to (``rock_work_links`` for a
 *  to-do; ``source_type='rock'`` + ``source_ref`` for an issue). Null when the
 *  item is unlinked. */
export interface LinkedRock {
  id: string;
  short_code: string;
  title: string;
  status: string;
  confidence: "explicit" | "inherited" | "suggested";
}

export interface Issue {
  id: string;
  tenant_id: string;
  team_id: string;
  title: string;
  description: string | null;
  author_id: string;
  /** Resolved user id for the issue's author profile. Read-only — the
   *  backend resolves author=current_user on create. Null when the author
   *  has no linked login. */
  author_user_id: string | null;
  owner_id: string | null;
  /** Resolved user id for the picked owner profile. Read-only — the backend
   *  resolves the create/update ``owner_id`` (a profile id) to a user. Null
   *  when the owner is unset or has no linked login. */
  owner_user_id: string | null;
  /** Owner display name, attached by the read model — an owner can be OFF
   *  this team's roster (cross-team flows), where the attendee lookup can
   *  never name them; the collapsed Owner picker seeds from this. Optional
   *  so an old backend in the deploy window maps cleanly to null. */
  owner_name?: string | null;
  status: IssueStatus;
  time_horizon: TimeHorizon;
  priority: number;
  source_type: IssueSourceType;
  source_ref: string | null;
  /** The rock a ``source_type='rock'`` issue was raised from, resolved from
   *  ``source_ref``. Optional so an old backend in the deploy window maps
   *  cleanly to absent. */
  linked_rock?: LinkedRock | null;
  /** Origin team of a cross-team copy (source_type='cross_l10_copy'),
   *  resolved by the read model from the source issue — the "Copied from
   *  {team}" chip reads this; null degrades the chip to a bare "Copied". */
  copied_from_team_name?: string | null;
  /** Drive artifact linkage: the backing artifact token + the subsection
   *  (dedup_key) this issue was staged from — feeds the artifact unfurl. */
  artifact_token?: string | null;
  dedup_key?: string | null;
  /** Drive feedback on a ``source_type === 'drive'`` item: the caller's own
   *  thumbs (null = hasn't voted) and the team-wide tally. Optional so an old
   *  backend in the deploy window maps cleanly to the empty state; non-Drive
   *  items never carry a vote and render no control. */
  my_feedback_rating?: "up" | "down" | null;
  feedback_counts?: DriveFeedbackCounts | null;
  identified_at: string | null;
  solved_at: string | null;
  solve_summary: string | null;
  /** True when ``solve_summary`` is a Kory suggestion (live scribe / transcript
   *  analyzer) rather than a human-written solve. Drives the "resolve?" nudge,
   *  which only fires on a suggested solve, and lets later analyzer slices
   *  refresh the suggestion without clobbering a human solve. */
  solve_summary_is_suggested: boolean;
  /** AI-generated recap of how this issue was discussed in its L10 — written by
   *  the transcript-ingest / live-scribe paths. Null until a transcript or
   *  scribe chunk has been processed for this issue. Drives the live "filled"
   *  state on the issue row. */
  discussion_summary: string | null;
  /** "Staged for this L10" timestamp — orthogonal to ``status``. Non-null =
   *  on the agenda. Cleared for the team when the meeting concludes. */
  staged_at: string | null;
  /** Archive marker. Null = active (shown in the default backlog); a
   *  timestamp = archived (hidden from default listings). Set by
   *  bulk-archive; cleared by restore. */
  dismissed_at: string | null;
  created_at: string;
}

export interface Headline {
  id: string;
  team_id: string;
  author_id: string;
  /** Resolved user id for the picked author profile. Read-only — the
   *  backend resolves the create/update ``author_id`` (a profile id) to a
   *  user. Null when the author has no linked login. */
  author_user_id: string | null;
  /** Author display name, attached by the read model for COPIED (cascade)
   *  rows only — their author is on the source team's roster, so this
   *  team's attendee lookup can never name them. Null for ordinary
   *  headlines (resolved from attendees instead). */
  author_name: string | null;
  kind: HeadlineKind;
  title: string;
  body: string | null;
  sentiment: Sentiment;
  cascaded_from_session_id: string | null;
  cascaded_from_team_id: string | null;
  cascaded_from_team_name: string | null;
  linked_issue_id: string | null;
  /** Set when the headline is archived (soft-hide); null when active. */
  dismissed_at: string | null;
  /** Set when the headline was read/announced in an L10 (team-wide); null
   *  when unread. Drives the prep board's post-meeting filter — a read
   *  headline stays visible in the live pane but drops off the prep board.
   *  Named ``read_at`` (not ``consumed_at``) to avoid colliding with
   *  ``CascadeDecision.consumed_at``, a distinct concept. */
  read_at: string | null;
  /** Origin provenance — mirrors l10_issues.source_type / source_ref.
   *  ``'todo'`` + source_ref=<todo.id>: created from a to-do via the
   *  type-switch composer; ``'rock'`` / ``'scorecard_kpi'`` + the rock id /
   *  KPI slug: made a headline from a feed update. Null for all other headlines. */
  source_type: string | null;
  source_ref: string | null;
  /** Kory's running recap of what the team said about this headline, written
   *  from a recorded meeting's live text. Read-only; optional so an old
   *  backend in the deploy window maps to no summary. */
  discussion_summary?: string | null;
  /** Drive artifact linkage: the backing artifact token + the subsection
   *  (dedup_key) this headline was staged from — feeds the artifact unfurl. */
  artifact_token?: string | null;
  dedup_key?: string | null;
  /** Drive feedback on a ``source_type === 'drive'`` item: the caller's own
   *  thumbs (null = hasn't voted) and the team-wide tally. Optional so an old
   *  backend in the deploy window maps cleanly to the empty state; non-Drive
   *  items never carry a vote and render no control. */
  my_feedback_rating?: "up" | "down" | null;
  feedback_counts?: DriveFeedbackCounts | null;
  created_at: string;
}

export interface CascadeDecision {
  id: string;
  source_session_id: string;
  target_team_id: string;
  /**
   * Denormalized display name for the target team. Backend populates it
   * on the create-cascade response (so the success toast can render the
   * team's real name); read-only snapshot endpoints leave it null and
   * the runner falls back to the target id.
   */
  target_team_name: string | null;
  content: string;
  status: CascadeStatus;
  delivered_session_id: string | null;
  consumed_at: string | null;
  created_at: string;
}

/**
 * UserTodo as the L10 runner needs to render it. The backend's UserTodo
 * shape has additional fields (sop_drive context, etc.); L10 only reads
 * the columns relevant to the meeting.
 *
 * ``source`` is a string union that mirrors the values written by
 * existing producers — anything outside the union (a future origin
 * like ``kory_chat``) collapses to ``"manual"`` in the wire→type
 * mapper. ``migrated_from_ninety`` is included so the Ninety-imported
 * to-dos can render with proper provenance instead of being misread
 * as user-typed manual entries; ``"drive"`` is the Drive gatekeeper's
 * provenance (a staged to-do backed by an artifact subsection) and
 * must round-trip so the artifact unfurl + Drive badge render.
 */
export interface L10UserTodo {
  id: string;
  title: string;
  /** User-authored notes (the row's ``subtitle`` column). Empty string
   *  when none — never the legacy ``"Due …"`` prefix (the backend reads
   *  that as a due-date fallback, not as notes). */
  notes: string;
  /** Priority bucket — the same ``normal`` / ``high`` / ``critical``
   *  string the agent surface stores; surfaced 1:1, no int mapping. */
  priority: "normal" | "high" | "critical";
  owner_id: string | null;
  owner_name: string | null;
  due_date: string | null;
  status: "pending" | "done" | "cancelled";
  source: "l10_meeting" | "sop_drive" | "drive" | "manual" | "migrated_from_ninety";
  /** Drive artifact linkage (Drive-staged to-dos): the backing artifact token + the
   *  subsection id (the proposal dedup_key, parsed from todo_key) — feeds the
   *  artifact unfurl. Null for non-Drive rows. */
  artifact_token?: string | null;
  drive_dedup_key?: string | null;
  /** Structured actions (Drive Build etc.) — mirrors the agent tray's
   *  ActionDTO shape; empty for plain rows. */
  actions?: TodoActionDTO[];
  /** The staging Drive's slug (Drive-staged rows) — the provenance chip. */
  drive_slug?: string | null;
  /** When the to-do was completed (``completed_at`` column); null while
   *  still open. The backend windows the team list off this, and the UI
   *  reads it for "done in this meeting" provenance. */
  completed_at: string | null;
  /** Who ticked it, when someone other than its owner did (the review names them); null otherwise. */
  completed_by_name?: string | null;
  /** Where it came from and who created it (server `todo_origin`); the same line every surface shows. */
  origin_line?: string | null;
  created_by_name?: string | null;
  /** When this (completed) to-do was reviewed in the team's L10 meeting.
   *  Null = completed but not yet reviewed in an L10 (the "awaiting L10
   *  review" state), or simply not completed. */
  reviewed_at: string | null;
  /** When a snooze expires (the ``dismissed_until`` column), ISO; null when
   *  not snoozed. The L10 prep board surfaces a snoozed to-do (future value)
   *  with a muted "Snoozed until [date]" pill so a parked team commitment
   *  stays visible rather than vanishing from the board. */
  dismissed_until: string | null;
  l10_issue_id: string | null;
  /** Back-link to the Headline this to-do was spawned from (Headline→To-Do
   *  cross-create). Undefined/null for non-headline-sourced to-dos. */
  l10_headline_id?: string | null;
  /** The rock this to-do is linked to, or null when unlinked. Optional so
   *  optimistic rows and an old backend in the deploy window map to absent. */
  linked_rock?: LinkedRock | null;
  /** The L10 team this to-do is bound to, or null for a TEAM-LESS (Overview)
   *  to-do. Drives the edit "Move / Copy to" routing: a team-bound to-do moves
   *  via ``moveTodo``, a team-less one binds via ``assignTodo``. */
  l10_team_id?: string | null;
  /** Human-readable name of ``l10_team_id`` — seeds the move/copy picker so the
   *  current L10 shows FILLED IN. Null for a team-less to-do. */
  l10_team_name?: string | null;
  created_at: string;
  /** True when the assignee isn't on this team's L10 roster — the to-do was
   *  delegated out from this L10. Stays bound to the team (it shows + completes
   *  here), but the runner segregates these into a "Delegated" section. */
  delegated?: boolean;
}

// ============================================================================
// Conclude recap (the in-app preview of what gets emailed)
// ============================================================================

/**
 * The L10 conclude recap — rendered by the in-app recap screen AND emailed to
 * present attendees on conclude. Both surfaces consume this same server-built
 * model (``GET /l10/sessions/{id}/recap``). Fields stay snake_case to match the
 * wire 1:1. The model's lists are complete — the screen is the persistent
 * browse surface for a past meeting; only the email clamps (server-side, at
 * render time), so ``*_overflow`` is always 0 on the wire. The screen also
 * carries fields the email omits (``reviewed_todos``, notes, dates).
 */
export interface L10RecapCommitment {
  id: string;
  title: string;
  owner_name: string | null;
  due_date: string | null;
  /** The to-do's notes (markdown). Like every recap field this is a live
   *  read of the current row, not a meeting-time snapshot. Null when empty. */
  notes: string | null;
  created_at: string | null;
  /** When the to-do was completed (null while open). */
  completed_at: string | null;
  /** The note left when the to-do was completed. */
  completion_note: string | null;
  /** The rock this to-do is linked to, or null. Optional for the deploy
   *  window (an old backend omits it). */
  linked_rock?: LinkedRock | null;
}

/** A pre-existing team to-do the meeting reviewed (recap screen only). */
export interface L10RecapReviewedTodo extends L10RecapCommitment {
  /** True when this meeting's To-dos review marked it done; false when it was
   *  still open at the meeting's end and rolled over. */
  done_at_review: boolean;
}

export interface L10RecapDecision {
  id: string;
  title: string;
  owner_name: string | null;
  /** The issue's own notes (the drawer's "Notes" field). */
  description: string | null;
  created_at: string | null;
  solved_at: string | null;
  /** The decision/resolution captured for the issue. */
  solve_summary: string | null;
  /** True when Kory proposed the resolution (vs. a human-entered one). */
  solve_summary_is_suggested: boolean;
  /** Kory's richer back-and-forth notes from the discussion (line-clamped on
   *  the recap screen). */
  discussion_summary: string | null;
}

export interface L10RecapHeadline {
  id: string;
  title: string;
  person_name: string | null;
  /** Complete notes shared with this headline during the meeting. */
  body: string | null;
}

export interface L10RecapCascade {
  id: string;
  target_team_name: string | null;
  content: string;
}

export interface L10RecapRating {
  /** Attendee profile id — a stable React key (display names can collide). */
  profile_id: string;
  name: string;
  rating: number | null;
}

export interface L10RecapHeardSuggestion {
  id: string;
  meeting_id: string;
  kind: "todo" | "issue" | "headline" | "idea";
  title: string;
  owner_name: string | null;
  due_date: string | null;
  explicit: boolean;
}

export interface L10Recap {
  session_id: string;
  team_name: string;
  /** ISO datetime of the meeting's start. Null for a session that hasn't
   *  started yet. The date shown to the user is ``meeting_date_label`` (this
   *  raw instant is kept for any consumer that needs the timestamp). */
  meeting_date: string | null;
  /** Pre-formatted meeting date in business (Eastern) time, e.g. "Mon, Jun 3".
   *  Rendered verbatim by both the screen and the email so they can never
   *  disagree. Null before the session starts. */
  meeting_date_label: string | null;
  duration_minutes: number | null;
  present_count: number;
  total_count: number;
  average_rating: number | null;
  commitments: L10RecapCommitment[];
  commitments_overflow: number;
  /** Pre-existing to-dos this meeting reviewed (done or rolled over). */
  reviewed_todos: L10RecapReviewedTodo[];
  decisions: L10RecapDecision[];
  decisions_overflow: number;
  headlines: L10RecapHeadline[];
  headlines_overflow: number;
  cascades: L10RecapCascade[];
  cascades_overflow: number;
  ratings: L10RecapRating[];
  heard_not_added: L10RecapHeardSuggestion[];
  meeting_review_url: string | null;
  /** The attached recording is still being written up; its heard rows land when it finishes. */
  recording_processing: boolean;
  carried_expired_count: number;
  /** One-click deep link back to this team's L10 board. */
  board_url: string;
  recap_url: string;
}

/** One row in a team's past-recaps list — a lightweight summary, not the full
 *  recap. ``recap_path`` is the in-app route to that session's recap screen. */
export interface L10RecapSummary {
  session_id: string;
  /** Business-time date label, e.g. "Mon, Jun 3". Null for an unstarted session. */
  meeting_date_label: string | null;
  duration_minutes: number | null;
  present_count: number;
  average_rating: number | null;
  /** Relative in-app route, e.g. ``/meetings/<slug>/recap/<id>``. */
  recap_path: string;
  /** The session's meeting type (``weekly_tenstar`` / ``project`` / …); null without a series. */
  meeting_type?: string | null;
}

/**
 * One of a team's meetings (``GET /l10/teams/{id}/series``): its weekly L10 or
 * its Project. ``agenda`` / ``default_duration_minutes`` are the meeting's
 * effective values; ``active_session_id`` is its in-progress session, if any.
 */
export interface L10MeetingSeries {
  id: string;
  team_id: string;
  meeting_type: "weekly_tenstar" | "project" | "sales_huddle" | string;
  agenda: AgendaItem[];
  agenda_is_custom: boolean;
  default_duration_minutes: number;
  /** For a Sales Huddle: today's running Daily Huddle session (kind ``sales``) for the team's brands. */
  active_session_id: string | null;
}

export interface SalesHuddleBrand {
  id: string;
  short_name: string;
}

/** ``GET /l10/teams/{id}/sales-huddle``: the team's Sales Huddle and the brands it covers.
 *  ``hidden_brands`` are the brands the caller cannot see; the huddle opens only when it is empty. */
export interface TeamSalesHuddle {
  series_id: string;
  team_id: string;
  brands: SalesHuddleBrand[];
  is_all_brands: boolean;
  hidden_brands: SalesHuddleBrand[];
}

/** One row of ``GET /l10/sales-huddles``: a Sales Huddle the caller can open, at
 *  ``/meetings/{team_slug}/sales-huddle``. Only teams whose brands the caller can all see are listed. */
export interface MySalesHuddle {
  team_id: string;
  team_name: string;
  team_slug: string;
  brands: SalesHuddleBrand[];
  is_all_brands: boolean;
}

// ============================================================================
// Scorecard + Rocks (read-only views for embed)
// ============================================================================

/**
 * Scorecard row shaped for the L10 embed. Real backend builds this from
 * the KPI registry + measurement service; the mock reflects the same shape
 * so the swap is one-line.
 */
export interface ScorecardRow {
  kpi_slug: string;
  kpi_name: string;
  goal_label: string;
  weeks: { week_iso: string; value: number | null; on_track: boolean }[];
}

export interface RockWeeklySnapshot {
  id: string;
  title: string;
  owner_name: string;
  status: "on_track" | "off_track" | "done";
  progress_pct: number;
  due_date: string;
}

// ============================================================================
// Request bodies
// ============================================================================

export interface CreateIssueBody {
  team_id: string;
  title: string;
  description?: string;
  time_horizon: TimeHorizon;
  source_type: IssueSourceType;
  source_ref?: string;
  /** Profile id of the issue owner. Optional — unassigned issues are
   * valid; the runner shows them as "unassigned" in the IDS sidebar. */
  owner_id?: string;
  /** Stage onto the current L10 agenda on create (sets ``staged_at``) —
   * orthogonal to ``status``, which stays ``open``. Set by L10 create
   * surfaces (prep + mid-meeting); the user can unstage from the list. */
  staged?: boolean;
  /** Kory artifact this issue wears as its BODY (create-from-artifact). The
   *  backend persists both onto the row (mirroring Drive-staged provenance) so
   *  the item durably renders the artifact body on reload. Omitted for a plain
   *  issue. */
  artifact_token?: string;
  artifact_id?: string;
}

export interface UpdateIssueBody {
  title?: string;
  description?: string;
  time_horizon?: TimeHorizon;
  /** Profile id of the issue owner — re-resolved to a user server-side.
   *  Empty string clears the owner. */
  owner_id?: string;
  /** Stage ↔ unstage for this L10 (orthogonal to status). Backend maps it to
   *  the ``staged_at`` timestamp. */
  staged?: boolean;
  /** Attach / replace (token) or remove (explicit null) the linked Kory
   *  artifact — send BOTH keys together (see ``editArtifactSlot``). */
  artifact_token?: string | null;
  artifact_id?: string | null;
}

/** Relocate an issue to one other L10 team (same id, new team). */
export interface MoveIssueBody {
  target_team_id: string;
}

/** Copy an issue onto one or more other L10 teams (source stays put). */
export interface CopyIssueToBody {
  target_team_ids: string[];
}

/** Relocate a headline to one other L10 team (same id, new team). */
export interface MoveHeadlineBody {
  target_team_id: string;
}

/** Copy a headline onto one or more other L10 teams (source stays put). */
export interface CopyHeadlineToBody {
  target_team_ids: string[];
}

/** Relocate a TEAM-BOUND to-do to one other L10 team (same id, new team). */
export interface MoveTodoBody {
  target_team_id: string;
}

/** Copy a to-do onto one or more other L10 teams (source stays put). */
export interface CopyTodoToBody {
  target_team_ids: string[];
}

/** Bind a TEAM-LESS (Overview) to-do to an L10 (the ticket's core fix). The
 *  backend 409s if the to-do is already team-bound, so the FE routes a
 *  team-bound to-do through ``moveTodo`` and a team-less one through this. */
export interface AssignTodoBody {
  target_team_id: string;
}

/** One boundary-cut audio segment uploaded to the live-scribe endpoint, tagged
 *  with the issue that was the active focus while it was captured. */
export interface ScribeChunkBody {
  sessionId: string;
  issueId: string;
  blob: Blob;
  /** Wall-clock window the segment covered (ISO-8601) — logged for audit. */
  startTs: string;
  endTs: string;
}

/** Result of summarizing one scribe chunk onto its issue. ``discussionSummary``
 *  is the issue's FULL running summary after the merge. ``written`` is false
 *  when the chunk had no substantive discussion (prior summary kept). */
export interface ScribeChunkResult {
  issue_id: string;
  discussed: boolean;
  written: boolean;
  discussion_summary: string | null;
  decision: string | null;
  action_items: string[];
}

export interface SolveIssueBody {
  solve_summary: string;
  // Creates a UserTodo with source='l10_meeting' + l10_issue_id wired;
  // the backend derives the to-do's l10_team_id from the issue's team.
  todo: {
    title: string;
    owner_id: string;
    due_date: string;
  };
}

export interface CreateHeadlineBody {
  team_id: string;
  title: string;
  body?: string;
  kind: HeadlineKind;
  sentiment: Sentiment;
  /** Profile id of the "Posted by" author. The backend resolves it to a
   *  user id (the decided identity policy: pickers show people, backend
   *  resolves to user_id). */
  author_id?: string;
  /** Broadcast targets: OTHER L10 team ids to also push this headline to.
   *  Each receives a ``kind="cascade"`` provenance copy back-linked to the
   *  source team. The backend re-validates the caller sees + is a member of
   *  every target; the source team is silently skipped if included. Omitted /
   *  empty = post to ``team_id`` only. */
  also_post_team_ids?: string[];
  /** Issue-origin → headline: link the headline to its originating issue
   *  atomically at create time. */
  linked_issue_id?: string;
  /** Todo-origin → headline provenance. source_type='todo' + source_ref=<todo.id>:
   *  the headline was promoted from a to-do via the type-switch composer. */
  source_type?: string;
  source_ref?: string;
  /** Kory artifact this headline wears as its BODY (create-from-artifact). See
   *  CreateIssueBody. Omitted for a plain headline. */
  artifact_token?: string;
  artifact_id?: string;
}

export interface UpdateHeadlineBody {
  title?: string;
  body?: string;
  kind?: HeadlineKind;
  sentiment?: Sentiment;
  /** Profile id of the "Posted by" author — re-resolved to a user server-side. */
  author_id?: string;
  /** Back-pointer to the Issue spawned by "Drop on Issues". UUID or null to clear. */
  linked_issue_id?: string | null;
  /** Attach / replace (token) or remove (explicit null) the linked Kory
   *  artifact — send BOTH keys together (see ``editArtifactSlot``). */
  artifact_token?: string | null;
  artifact_id?: string | null;
}

export interface CreateTodoBody {
  title: string;
  owner_profile_id: string;
  due_date?: string | null;
  /** The L10 team this to-do belongs to (first-class membership). The
   *  prep board / composer pass it directly; the Solve path omits it and
   *  lets the backend derive the team from ``l10_issue_id``. */
  l10_team_id?: string | null;
  l10_issue_id?: string | null;
  /** Back-link to the Headline this to-do follows up on (Headline→To-Do
   *  cross-create). Backend derives the team from the headline when this is
   *  set and ``l10_team_id`` is omitted. */
  l10_headline_id?: string | null;
  /** Link the new to-do to this rock (Rock Review's "Create linked to-do").
   *  The backend 404s an out-of-scope rock and 422s a closed one. */
  rock_id?: string | null;
  /** User-authored notes → the to-do's subtitle (composer can set on create). */
  notes?: string | null;
  /** Priority bucket the composer picked — the same ``normal`` / ``high`` /
   *  ``critical`` string the to-do stores. Omitted ⇒ the backend defaults to
   *  ``normal`` (the column server_default). Lets an Overview to-do created
   *  INTO an L10 carry its High/Critical priority instead of always landing as
   *  normal. */
  priority?: "normal" | "high" | "critical";
  /** Optional grouping axis for the Todos board (list groups / lanes). */
  group?: string | null;
  /** Kory artifact this to-do wears as its BODY (create-from-artifact). See
   *  CreateIssueBody. Omitted for a plain to-do. */
  artifact_token?: string;
  artifact_id?: string;
}

export interface CreateCascadeBody {
  source_session_id: string;
  target_team_id: string;
  content: string;
}

// ============================================================================
// Item links (generic many-to-many link substrate)
// ============================================================================

export type L10ItemKind = "headline" | "issue" | "todo";

export interface L10ItemRef {
  kind: L10ItemKind;
  id: string;
}

export interface L10ItemLinkOther {
  kind: L10ItemKind;
  id: string;
  title: string;
}

/** One row from GET /l10/teams/{team_id}/item-links */
export interface L10ItemLink {
  link_id: string;
  other: L10ItemLinkOther;
}

// ============================================================================
// Meet transcript pull (post-meeting attribution of the Google Meet transcript)
// ============================================================================

/** Body for ``POST /l10/sessions/{id}/pull-transcript``. ``organizerEmail``
 *  overrides the impersonated organizer; omit (or blank) to use the session
 *  facilitator's linked-user email. The Meet space comes from the team config,
 *  not the request. */
export interface MeetTranscriptPullRequest {
  organizerEmail?: string;
}

/** One issue's attribution result from a transcript ingest/pull, echoed back so
 *  the run can be eyeballed. ``written`` is false for an issue the transcript
 *  didn't discuss (prior summary kept). */
export interface TranscriptIssueSummary {
  issue_id: string;
  title: string;
  discussed: boolean;
  discussion_summary: string | null;
  decision: string | null;
  action_items: string[];
  owner_hint: string | null;
  written: boolean;
}

/** Result of pulling + attributing a Meet transcript across the agenda.
 *  ``issues_written`` of ``issues_on_agenda`` got a fresh summary; ``unattributed``
 *  is a free-text bucket of discussion that didn't map to a staged issue. */
export interface TranscriptIngestResponse {
  session_id: string;
  issues_on_agenda: number;
  issues_written: number;
  issues: TranscriptIssueSummary[];
  unattributed: string | null;
}

/** Status of the most-recent transcript pull job for a session.
 *  ``status`` is null when no pull has been triggered, or one of
 *  "queued" | "running" | "completed" | "failed".
 *
 *  ``issues_on_agenda`` is only set when ``status === "completed"``: it is the
 *  number of attribution candidates the pull found.  Zero means the transcript
 *  was fetched but no agenda issues existed to fill in (attribution skipped). */
export interface TranscriptPullStatus {
  session_id: string;
  status: "queued" | "running" | "completed" | "failed" | null;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  issues_on_agenda: number | null;
}
